import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as authActions from '../../Redux/Auth/AuthActions.js';
import ContactForm from '../../Utils/Forms/ContactForm/contact-form-component.js';
import { getContactData } from '../../Public/contact-data.js';
import SystemDataService from '../../Services/SystemDataService.js';
import MapFrame from '../../Utils/Map/map-frame-component.js';
import MediaComponent from '../../Utils/Media/media-component.js';

const PinIcon = () => (
    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
    </svg>
);

function Contact() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const systemData = useSelector((state) => state.systemData || {});
    const { dir = 'ltr', language = 'en', links = [], businessInfo = {} } = systemData;
    const txt = useMemo(() => getContactData(language), [language]);

    const BusinessMaps = SystemDataService.byLabel(links, 'Business Maps');
    const heroImage = SystemDataService.byLabel(links, 'Contact Hero') || SystemDataService.byLabel(links, 'Hero BG');
    const phone = SystemDataService.byLabel(links, 'Phone');
    const emailLink = SystemDataService.byLabel(links, 'Email');
    const whatsapp = SystemDataService.byLabel(links, 'WhatsApp');

    const locationName    = businessInfo?.businessName || txt.location?.name;
    const locationAddress = businessInfo?.address      || BusinessMaps?.description || txt.location?.address;

    const contactMethods = [
        { link: phone,     id: 'call' },
        { link: emailLink, id: 'email' },
        { link: whatsapp,  id: 'whatsapp' },
    ].filter(m => m.link?.url);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(authActions.isLoggedIn());
    }, [dispatch]);

    return (
        <div dir={dir} className="relative w-full flex flex-col bg-gradient-to-t from-primary-500 to-primary-700">

            {/* ── Section 1: Hero — full-width media ── */}
            <div className="relative overflow-hidden min-h-[280px]">
                {/* Full-width background image */}
                <div className="absolute inset-0">
                    {heroImage?.image && (
                        <MediaComponent
                            src={heroImage.image}
                            alt=""
                            className="w-full h-full object-cover opacity-40"
                            wrapperClassName="w-full h-full"
                            useSekeleton={false}
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-600 via-primary-600/60 to-transparent" />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-4 py-8">
                    <span className="text-secondary-500 text-xs font-black uppercase tracking-widest block mb-3 border-b border-secondary-500 w-fit pb-1">
                        {txt.badge}
                    </span>
                    <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight text-balance mb-4 text-left">
                        {txt.hero.title}<br />
                        <span className="text-secondary-500">{txt.hero.titleHighlight}</span>
                    </h1>
                    <p className="text-neutral-300 text-base md:text-lg max-w-xl text-pretty leading-relaxed text-left">
                        {txt.hero.subtitle}{' '}
                        <span className="text-secondary-500 font-semibold">{txt.hero.subtitleHighlight}</span>
                    </p>
                </div>
            </div>

            {/* ── Section 2: Contact methods — white rounded card ── */}
            <div className="px-4 py-8">
                <div className="max-w-[1400px] mx-auto bg-white rounded-2xl">
                    <div className="flex flex-col md:flex-row">
                        {contactMethods.map((method) => (
                            <a
                                key={method.id}
                                href={method.link.url}
                                target={method.id === 'whatsapp' ? '_blank' : undefined}
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center px-6 py-5 md:py-6 hover:bg-gray-50 transition-colors border-t md:border-t-0 md:border-l border-gray-200 first:border-t-0 first:md:border-l-0 first:rounded-tl-2xl first:rounded-bl-2xl last:rounded-tr-2xl last:rounded-br-2xl"
                            >
                                <div className="grid grid-cols-[48px_1fr] md:grid-cols-[56px_1fr] items-center gap-4 w-full max-w-xs mx-auto">
                                    <div className="flex items-center justify-center">
                                        {method.link.image ? (
                                            <MediaComponent
                                                src={method.link.image}
                                                alt={method.link.label}
                                                className="w-6 h-6 md:w-7 md:h-7 object-contain"
                                                wrapperClassName="flex items-center justify-center"
                                                useSekeleton={false}
                                            />
                                        ) : (
                                            <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-primary-600" />
                                        )}
                                    </div>

                                    <div className="flex flex-col text-left">
                                        <span className="text-sm font-semibold text-primary-900 leading-tight">
                                            {method.link.label}
                                        </span>
                                        <span className="text-base md:text-lg font-bold text-secondary-500 leading-tight">
                                            {method.link.description}
                                        </span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Section 3: Form + Map ── */}
            <div className="px-4 md:px-6 pb-8 md:pb-12">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">

                    {/* Left: form card */}
                    <div className="bg-white rounded-2xl p-6 md:p-8">
                        <ContactForm onSuccess={() => navigate('/thank-you')} />
                    </div>

                    {/* Right: location + map inside primary-800 card */}
                    <div className="bg-primary-800 rounded-2xl p-6 flex flex-col gap-4">
                        <div>
                            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-2 text-left">
                                {txt.location?.title || 'OUR LOCATION'}
                            </h3>
                            <div className="w-20 h-0.5 bg-secondary-500 mb-3" />
                            <div className="flex items-start gap-2">
                                <div className="text-secondary-500 mt-0.5">
                                    <PinIcon />
                                </div>
                                <div className="text-left">
                                    <p className="text-white font-black text-sm uppercase tracking-wide">
                                        {locationName}
                                    </p>
                                    {locationAddress && (
                                        <p className="text-white/60 text-sm font-normal mt-0.5 whitespace-pre-line leading-relaxed">
                                            {locationAddress}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="rounded-xl overflow-hidden flex-1 min-h-[280px]">
                            <MapFrame
                                url={BusinessMaps?.url}
                                interactive={true}
                                className="w-full h-full"
                            />
                        </div>
                    </div>

                </div>
            </div>

        </div>
    );
}

export default Contact;
